import React, { useEffect, useMemo, useState } from "react";
import api, { dateDK, formatErr } from "../lib/api";
import { Input } from "../components/ui/input";
import { Search, ArrowUp, ArrowDown, EyeOff, RotateCcw, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => {
  if (v == null || v === 0) return "—";
  return new Intl.NumberFormat("da-DK", { maximumFractionDigits: 0 }).format(Math.round(v));
};

const STATUS_META = {
  active: { label: "Aktive biler", testid: "section-active", accent: "text-emerald-400" },
  lager:  { label: "Lagerbiler",   testid: "section-lager",  accent: "text-[#C8A661]" },
  sold:   { label: "Solgte biler", testid: "section-sold",   accent: "text-zinc-400" },
};
const STATUS_ORDER = ["active", "lager", "sold"];

// Avance på revisor-fane: ren købspris vs salgspris (ingen 3.500 prep eller ekstra omk.).
function calcRow(c) {
  const purchase = Number(c.purchase_price || 0);
  const sale = Number(c.sale_price || 0);
  const isSold = c.status === "sold" && sale > 0 && purchase > 0;
  const margin = isSold ? sale - purchase : 0;
  const brugtmoms = isSold && margin > 0 ? margin * 0.20 : 0;
  return {
    ...c,
    _purchase: purchase,
    _sale: sale,
    _margin: isSold ? margin : null,
    _brugtmoms: isSold ? brugtmoms : null,
    _isSold: isSold,
    _excluded: !!c.accountant_excluded,
  };
}

export default function Accountant() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/cars", { params: { include_revenue: false } })
      .then((r) => setCars(r.data))
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const enriched = useMemo(() => cars.map(calcRow), [cars]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return enriched;
    return enriched.filter((c) => {
      const hay = [
        c.license_plate, c.make, c.model, c.variant, c.year,
        c.purchase_date, c.sale_date, c.accountant_note,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [enriched, search]);

  const grouped = useMemo(() => {
    const buckets = { active: [], lager: [], sold: [], excluded: [] };
    for (const c of filtered) {
      if (c._excluded) {
        buckets.excluded.push(c);
        continue;
      }
      const key = STATUS_ORDER.includes(c.status) ? c.status : "active";
      buckets[key].push(c);
    }
    if (sortKey) {
      const dir = sortDir === "asc" ? 1 : -1;
      const get = (c) => {
        switch (sortKey) {
          case "plate": return c.license_plate || "";
          case "bil": return `${c.make || ""} ${c.model || ""}`;
          case "purchase_price": return c._purchase || 0;
          case "purchase_date": return c.purchase_date || "";
          case "sale_price": return c._sale || 0;
          case "sale_date": return c.sale_date || "";
          case "margin": return c._margin == null ? -Infinity : c._margin;
          case "brugtmoms": return c._brugtmoms == null ? -Infinity : c._brugtmoms;
          default: return 0;
        }
      };
      Object.keys(buckets).forEach((k) => {
        buckets[k].sort((a, b) => {
          const av = get(a), bv = get(b);
          if (typeof av === "string") return av.localeCompare(bv) * dir;
          return ((av || 0) - (bv || 0)) * dir;
        });
      });
    } else {
      buckets.active.sort((a, b) => (a.license_plate || "").localeCompare(b.license_plate || ""));
      buckets.lager.sort((a, b) => (a.license_plate || "").localeCompare(b.license_plate || ""));
      buckets.sold.sort((a, b) => (b.sale_date || "").localeCompare(a.sale_date || ""));
      buckets.excluded.sort((a, b) => (a.license_plate || "").localeCompare(b.license_plate || ""));
    }
    return buckets;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const setExcluded = async (car, value) => {
    try {
      await api.patch(`/cars/${car.id}`, { accountant_excluded: value });
      // Optimistisk opdatering — undgår fuld reload
      setCars((arr) => arr.map((x) => x.id === car.id ? { ...x, accountant_excluded: value } : x));
      toast.success(value
        ? `${car.license_plate} flyttet til "Ikke relevant"`
        : `${car.license_plate} sat tilbage på listen`);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const startEditNote = (car) => {
    setEditingNoteId(car.id);
    setNoteDraft(car.accountant_note || "");
  };
  const cancelEditNote = () => {
    setEditingNoteId(null);
    setNoteDraft("");
  };
  const saveNote = async (car) => {
    try {
      await api.patch(`/cars/${car.id}`, { accountant_note: noteDraft });
      setCars((arr) => arr.map((x) => x.id === car.id ? { ...x, accountant_note: noteDraft } : x));
      toast.success("Note gemt");
      cancelEditNote();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const totalsSold = useMemo(() => {
    let purchase = 0, sale = 0, margin = 0, moms = 0;
    for (const c of grouped.sold) {
      purchase += c._purchase || 0;
      sale += c._sale || 0;
      if (c._margin != null) margin += c._margin;
      if (c._brugtmoms != null) moms += c._brugtmoms;
    }
    return { purchase, sale, margin, moms };
  }, [grouped.sold]);

  const counts = {
    active: grouped.active.length,
    lager: grouped.lager.length,
    sold: grouped.sold.length,
    excluded: grouped.excluded.length,
    total: grouped.active.length + grouped.lager.length + grouped.sold.length,
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1400px]" data-testid="accountant-page">
      <div className="mb-6 sm:mb-10">
        <div className="overline text-zinc-400">Biler</div>
        <h1 className="headline text-3xl sm:text-4xl lg:text-5xl mt-2">Revisor.</h1>
        <p className="text-zinc-500 text-xs sm:text-sm mt-1">
          Ren oversigt til revisor — kun købs- og salgspris (uden 3.500 i klargøring).
          Skjul biler der ikke er relevante. Skriv noter pr. bil.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søg plade, mærke, model, årgang, dato, note…"
            className="pl-9 rounded-none border-zinc-700"
            data-testid="accountant-search"
          />
        </div>
        <div className="flex gap-2 text-xs text-zinc-500" data-testid="accountant-counts">
          <Pill label="Aktive" value={counts.active} />
          <Pill label="Lager" value={counts.lager} />
          <Pill label="Solgte" value={counts.sold} />
          <Pill label="I alt" value={counts.total} accent />
          {counts.excluded > 0 && <Pill label="Ikke relevant" value={counts.excluded} />}
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Indlæser…</div>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full text-sm min-w-[1100px]">
            <thead>
              <tr className="border-b border-zinc-800 text-zinc-500 text-[10px] uppercase tracking-wider">
                <th className="text-left py-3 pr-3"><SortBtn k="plate" cur={sortKey} dir={sortDir} onClick={toggleSort}>Plade</SortBtn></th>
                <th className="text-left py-3 pr-3"><SortBtn k="bil" cur={sortKey} dir={sortDir} onClick={toggleSort}>Bil</SortBtn></th>
                <th className="text-right py-3 pr-3"><SortBtn k="purchase_price" cur={sortKey} dir={sortDir} onClick={toggleSort}>Købspris</SortBtn></th>
                <th className="text-left py-3 pr-3"><SortBtn k="purchase_date" cur={sortKey} dir={sortDir} onClick={toggleSort}>Købsdato</SortBtn></th>
                <th className="text-right py-3 pr-3"><SortBtn k="sale_price" cur={sortKey} dir={sortDir} onClick={toggleSort}>Salgspris</SortBtn></th>
                <th className="text-left py-3 pr-3"><SortBtn k="sale_date" cur={sortKey} dir={sortDir} onClick={toggleSort}>Salgsdato</SortBtn></th>
                <th className="text-right py-3 pr-3"><SortBtn k="margin" cur={sortKey} dir={sortDir} onClick={toggleSort}>Avance</SortBtn></th>
                <th className="text-right py-3 pr-3"><SortBtn k="brugtmoms" cur={sortKey} dir={sortDir} onClick={toggleSort}>Moms af avance</SortBtn></th>
                <th className="text-left py-3 pr-3 w-[200px]">Note til revisor</th>
                <th className="text-right py-3 pr-3 w-[60px]"></th>
              </tr>
            </thead>
            <tbody>
              {STATUS_ORDER.map((statusKey) => {
                const list = grouped[statusKey];
                const meta = STATUS_META[statusKey];
                if (list.length === 0) return null;
                return (
                  <React.Fragment key={statusKey}>
                    <tr data-testid={meta.testid}>
                      <td colSpan={10} className="pt-8 pb-2">
                        <div className={"font-display tracking-tighter text-lg " + meta.accent}>
                          {meta.label}
                          <span className="ml-2 text-zinc-500 text-xs font-sans tracking-normal">{list.length}</span>
                        </div>
                      </td>
                    </tr>
                    {list.map((c) => (
                      <Row
                        key={c.id}
                        c={c}
                        editing={editingNoteId === c.id}
                        noteDraft={noteDraft}
                        setNoteDraft={setNoteDraft}
                        onStartEdit={() => startEditNote(c)}
                        onSaveNote={() => saveNote(c)}
                        onCancelNote={cancelEditNote}
                        onExclude={() => setExcluded(c, true)}
                      />
                    ))}
                  </React.Fragment>
                );
              })}

              {grouped.excluded.length > 0 && (
                <React.Fragment>
                  <tr data-testid="section-excluded">
                    <td colSpan={10} className="pt-8 pb-2">
                      <div className="font-display tracking-tighter text-lg text-zinc-500">
                        Ikke relevant
                        <span className="ml-2 text-zinc-600 text-xs font-sans tracking-normal">{grouped.excluded.length}</span>
                      </div>
                      <div className="text-[11px] text-zinc-600">
                        Skjult fra revisor-listen. Påvirker ikke andre faner.
                      </div>
                    </td>
                  </tr>
                  {grouped.excluded.map((c) => (
                    <Row
                      key={c.id}
                      c={c}
                      muted
                      editing={editingNoteId === c.id}
                      noteDraft={noteDraft}
                      setNoteDraft={setNoteDraft}
                      onStartEdit={() => startEditNote(c)}
                      onSaveNote={() => saveNote(c)}
                      onCancelNote={cancelEditNote}
                      onRestore={() => setExcluded(c, false)}
                    />
                  ))}
                </React.Fragment>
              )}

              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-zinc-500 text-sm">
                    Ingen biler matcher søgningen.
                  </td>
                </tr>
              )}
            </tbody>
            {grouped.sold.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-zinc-700 font-display">
                  <td colSpan={2} className="pt-4 text-zinc-500 uppercase tracking-wider text-[10px]">Total · solgte</td>
                  <td className="text-right tabular-nums text-zinc-300 pt-4 pr-3">{fmt(totalsSold.purchase)}</td>
                  <td></td>
                  <td className="text-right tabular-nums text-zinc-300 pt-4 pr-3">{fmt(totalsSold.sale)}</td>
                  <td></td>
                  <td className={"text-right tabular-nums pt-4 pr-3 " + (totalsSold.margin >= 0 ? "text-emerald-400" : "text-red-400")}>
                    {fmt(totalsSold.margin)}
                  </td>
                  <td className="text-right tabular-nums pt-4 pr-3 text-[#C8A661]">{fmt(totalsSold.moms)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

function Row({ c, muted, editing, noteDraft, setNoteDraft, onStartEdit, onSaveNote, onCancelNote, onExclude, onRestore }) {
  return (
    <tr
      className={"border-b border-zinc-900 transition-colors " + (muted ? "opacity-50 hover:opacity-80" : "hover:bg-[#1F1F1F]")}
      data-testid={`accountant-row-${c.license_plate}`}
    >
      <td className="py-3 pr-3 font-display font-bold tracking-tight text-white">{c.license_plate}</td>
      <td className="py-3 pr-3 text-zinc-300">
        <div>{[c.make, c.model].filter(Boolean).join(" ") || "—"}</div>
        {c.year ? <div className="text-[11px] text-zinc-500">{c.year}</div> : null}
      </td>
      <td className="py-3 pr-3 text-right tabular-nums text-zinc-200">{fmt(c._purchase)}</td>
      <td className="py-3 pr-3 text-zinc-400">{c.purchase_date ? dateDK(c.purchase_date) : "—"}</td>
      <td className="py-3 pr-3 text-right tabular-nums text-zinc-200">{c._isSold ? fmt(c._sale) : "—"}</td>
      <td className="py-3 pr-3 text-zinc-400">{c.sale_date ? dateDK(c.sale_date) : "—"}</td>
      <td className={"py-3 pr-3 text-right tabular-nums " + (
        c._margin == null ? "text-zinc-600" :
        c._margin >= 0 ? "text-emerald-400" : "text-red-400"
      )}>
        {c._margin == null ? "—" : fmt(c._margin)}
      </td>
      <td className="py-3 pr-3 text-right tabular-nums text-[#C8A661]">{c._brugtmoms == null ? "—" : fmt(c._brugtmoms)}</td>
      <td className="py-3 pr-3">
        {editing ? (
          <div className="flex items-center gap-1">
            <Input
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") onSaveNote();
                else if (e.key === "Escape") onCancelNote();
              }}
              className="rounded-none border-zinc-700 h-8 text-xs"
              data-testid={`note-input-${c.license_plate}`}
              placeholder="Skriv note…"
            />
            <button onClick={onSaveNote} className="text-emerald-400 hover:text-emerald-300 p-1" data-testid={`note-save-${c.license_plate}`} title="Gem">
              <Check className="w-3.5 h-3.5" />
            </button>
            <button onClick={onCancelNote} className="text-zinc-500 hover:text-white p-1" data-testid={`note-cancel-${c.license_plate}`} title="Annullér">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            onClick={onStartEdit}
            className="text-left w-full group"
            data-testid={`note-edit-${c.license_plate}`}
          >
            {c.accountant_note ? (
              <span className="text-zinc-300 text-xs whitespace-pre-wrap group-hover:text-white">{c.accountant_note}</span>
            ) : (
              <span className="text-zinc-600 text-xs flex items-center gap-1 group-hover:text-zinc-400">
                <Pencil className="w-3 h-3" /> tilføj note
              </span>
            )}
          </button>
        )}
      </td>
      <td className="py-3 pr-3 text-right whitespace-nowrap">
        {muted ? (
          <button
            onClick={onRestore}
            className="text-zinc-500 hover:text-emerald-400 p-1.5"
            data-testid={`accountant-restore-${c.license_plate}`}
            title="Sæt tilbage på listen"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={onExclude}
            className="text-zinc-600 hover:text-red-400 p-1.5"
            data-testid={`accountant-exclude-${c.license_plate}`}
            title="Flyt til Ikke relevant"
          >
            <EyeOff className="w-4 h-4" />
          </button>
        )}
      </td>
    </tr>
  );
}

function Pill({ label, value, accent = false }) {
  return (
    <div className={"px-2.5 py-1 border " + (accent ? "border-[#C8A661]/40 text-[#C8A661]" : "border-zinc-800 text-zinc-400")}>
      <span className="uppercase tracking-wider text-[10px]">{label}</span>{" "}
      <span className="font-mono font-bold ml-1">{value}</span>
    </div>
  );
}

function SortBtn({ k, cur, dir, onClick, children }) {
  const active = cur === k;
  const arrow = active ? (dir === "asc" ? <ArrowUp className="w-3 h-3 inline ml-1" /> : <ArrowDown className="w-3 h-3 inline ml-1" />) : null;
  return (
    <button
      type="button"
      onClick={() => onClick(k)}
      className={"flex items-center gap-1 hover:text-white transition-colors " + (active ? "text-white" : "")}
      data-testid={`sort-${k}`}
    >
      {children}{arrow}
    </button>
  );
}
